import { caseApiRequest, dataPayloadErrorMessage, isDataPayloadSuccess } from './apiClient';
import type { ApiResult } from './types';
import { isCaseRequestUuid } from '../pages/task-case/taskCaseUpdateHelpers';

export type ClosureApproverUserRow = {
  user_id?: string;
  userId?: string;
  display_name?: string;
  displayName?: string;
  username?: string;
  verification_email?: string;
  verificationEmail?: string;
  roles?: string[];
  designation?: string | null;
};

function strField(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function normalizeUsers(raw: unknown): ClosureApproverUserRow[] {
  if (!Array.isArray(raw)) return [];
  const out: ClosureApproverUserRow[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    out.push(item as ClosureApproverUserRow);
  }
  return out;
}

function appCode(input?: string, fallbackEnv?: string): string {
  return (input ?? fallbackEnv ?? 'INTRANET').trim();
}

function boolField(row: Record<string, unknown>, ...keys: string[]): boolean {
  for (const k of keys) {
    const v = row[k];
    if (v === true || v === false) return v;
    if (v === 'true') return true;
    if (v === 'false') return false;
  }
  return false;
}

export type ExistingClosureAssignment = {
  authorizedClosureApproverId?: string;
  authorizedClosureApproverLabel?: string;
  nonAuthorizedClosureApproverId?: string;
  nonAuthorizedClosureApproverLabel?: string;
  physicalFileMovementRequired: boolean;
  documentMoverId?: string;
  documentMoverLabel?: string;
};

export type ValidateClosureApproverClickResult = {
  alreadyClicked: boolean;
  existingAssignment?: ExistingClosureAssignment;
};

function userIdFromRow(row: unknown): string {
  if (!row || typeof row !== 'object') return '';
  return strField(row as Record<string, unknown>, 'user_id', 'userId');
}

function displayNameFromRow(row: unknown): string {
  if (!row || typeof row !== 'object') return '';
  const r = row as Record<string, unknown>;
  return strField(r, 'display_name', 'displayName') || userIdFromRow(row);
}

function designationFromRow(row: unknown): string {
  if (!row || typeof row !== 'object') return '';
  return strField(row as Record<string, unknown>, 'designation');
}

function formatNameWithDesignation(name: string, designation: string): string {
  return `${name} (${designation || '-'})`;
}

function nonAuthorizedLabelFromRow(row: unknown): string {
  const id = userIdFromRow(row);
  if (!id) return '';
  const name = displayNameFromRow(row) || id;
  return formatNameWithDesignation(name, designationFromRow(row));
}

function normalizeExistingClosureAssignment(raw: unknown): ExistingClosureAssignment | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;

  const authorizedRow = o.authorized_closure_approver ?? o.authorizedClosureApprover;
  const nonAuthorizedRow = o.non_authorized_closure_approver ?? o.nonAuthorizedClosureApprover;
  const documentMoverRow = o.document_mover ?? o.documentMover;

  const authorizedClosureApproverId = userIdFromRow(authorizedRow);
  const nonAuthorizedClosureApproverId = userIdFromRow(nonAuthorizedRow);
  const documentMoverId = userIdFromRow(documentMoverRow);

  if (!authorizedClosureApproverId && !nonAuthorizedClosureApproverId) return undefined;

  return {
    authorizedClosureApproverId: authorizedClosureApproverId || undefined,
    authorizedClosureApproverLabel: authorizedClosureApproverId
      ? displayNameFromRow(authorizedRow)
      : undefined,
    nonAuthorizedClosureApproverId: nonAuthorizedClosureApproverId || undefined,
    nonAuthorizedClosureApproverLabel: nonAuthorizedClosureApproverId
      ? nonAuthorizedLabelFromRow(nonAuthorizedRow)
      : undefined,
    physicalFileMovementRequired: boolField(o, 'physicalFileMovementRequired', 'physical_file_movement_required'),
    documentMoverId: documentMoverId || undefined,
    documentMoverLabel: documentMoverId ? displayNameFromRow(documentMoverRow) : undefined,
  };
}

function normalizeValidateClosureApproverClickPayload(raw: unknown): ValidateClosureApproverClickResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const alreadyClicked = boolField(o, 'alreadyClicked', 'already_clicked');
  return {
    alreadyClicked,
    existingAssignment: alreadyClicked ? normalizeExistingClosureAssignment(o) : undefined,
  };
}

/**
 * CMS-AUQ-3
 * GET /v1/case-requests/users/closure-approvers/authorized?taskId=...
 */
export async function getAuthorizedClosureApprovers(input: {
  taskId: string;
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<ClosureApproverUserRow[]>> {
  const taskId = input.taskId.trim();
  if (!isCaseRequestUuid(taskId)) return { success: false, message: 'Invalid task id.' };

  const applicationCode = appCode(input.applicationCode, import.meta.env.VITE_APP_CODE_CLOSURE_APPROVERS ?? 'INTRANET');
  if (!applicationCode) return { success: false, message: 'Missing application code.' };

  const res = await caseApiRequest<unknown>(`/users/closure-approvers/authorized?taskId=${encodeURIComponent(taskId)}`, {
    headers: { applicationCode, 'application-code': applicationCode },
    signal: input.signal,
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeUsers(res.data) };
}

/**
 * CMS-AUQ-5
 * GET /v1/case-requests/users/junior-counsels
 */
export async function listJuniorCounsels(input?: {
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<ClosureApproverUserRow[]>> {
  const applicationCode = appCode(input?.applicationCode, import.meta.env.VITE_APP_CODE_JUNIOR_COUNSELS ?? 'INTRANET');
  if (!applicationCode) return { success: false, message: 'Missing application code.' };

  const res = await caseApiRequest<unknown>('/users/junior-counsels', {
    headers: { applicationCode, 'application-code': applicationCode },
    signal: input?.signal,
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeUsers(res.data) };
}

/**
 * CMS-AUQ-4
 * GET /v1/case-requests/users/closure-approvers/non-authorized
 */
export async function getNonAuthorizedClosureApprovers(input?: {
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<ClosureApproverUserRow[]>> {
  const applicationCode = appCode(input?.applicationCode, import.meta.env.VITE_APP_CODE_CLOSURE_APPROVERS ?? 'INTRANET');
  if (!applicationCode) return { success: false, message: 'Missing application code.' };

  const res = await caseApiRequest<unknown>('/users/closure-approvers/non-authorized', {
    headers: { applicationCode, 'application-code': applicationCode },
    signal: input?.signal,
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeUsers(res.data) };
}

/**
 * CMS-CCA-10
 * POST /v1/case-requests/workflow-tasks/validate-closure-approver-click
 *
 * Check whether the current user has already invoked CMS-CCA-7 for the given task flow.
 */
export async function validateClosureApproverClick(input: {
  taskId: string;
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<ValidateClosureApproverClickResult>> {
  const taskId = input.taskId.trim();
  if (!isCaseRequestUuid(taskId)) return { success: false, message: 'Invalid task id.' };

  const applicationCode = appCode(
    input.applicationCode,
    import.meta.env.VITE_APP_CODE_VALIDATE_CLOSURE_APPROVER_CLICK ?? 'INTRANET',
  );
  if (!applicationCode) return { success: false, message: 'Missing application code.' };

  const res = await caseApiRequest<unknown>('/workflow-tasks/validate-closure-approver-click', {
    method: 'POST',
    headers: { applicationCode, 'application-code': applicationCode },
    body: { task_id: taskId },
    signal: input.signal,
  });

  if (!res.ok) return { success: false, message: res.message };

  const data = normalizeValidateClosureApproverClickPayload(res.data);
  if (!data) return { success: false, message: 'Invalid validation response.' };
  return { success: true, data };
}

/**
 * CMS-CCA-7
 * POST /v1/case-requests/workflow-tasks/assign-closure-approver
 */
export async function assignClosureApprover(input: {
  workflowInstanceStageUserTaskId: string;
  authorizedClosureApproverId?: string;
  nonAuthorizedClosureApproverId?: string;
  physicalFileMovementRequired: boolean;
  documentMoverId?: string;
  applicationCode?: string;
}): Promise<ApiResult<void>> {
  const workflowInstanceStageUserTaskId = input.workflowInstanceStageUserTaskId.trim();
  const authorizedClosureApproverId = (input.authorizedClosureApproverId ?? '').trim();
  const nonAuthorizedClosureApproverId = (input.nonAuthorizedClosureApproverId ?? '').trim();
  const documentMoverId = (input.documentMoverId ?? '').trim();

  if (!isCaseRequestUuid(workflowInstanceStageUserTaskId)) return { success: false, message: 'Invalid user task id.' };
  if (!authorizedClosureApproverId && !nonAuthorizedClosureApproverId) {
    return { success: false, message: 'Closure approver is required.' };
  }
  if (authorizedClosureApproverId && nonAuthorizedClosureApproverId) {
    return { success: false, message: 'Select only one closure approver.' };
  }
  if (input.physicalFileMovementRequired && !documentMoverId) {
    return { success: false, message: 'Document mover is required.' };
  }

  const applicationCode = appCode(input.applicationCode, import.meta.env.VITE_APP_CODE_ASSIGN_CLOSURE_APPROVER ?? 'INTRANET');
  if (!applicationCode) return { success: false, message: 'Missing application code.' };

  const res = await caseApiRequest<{ status?: string; message?: string } | unknown>(
    '/workflow-tasks/assign-closure-approver',
    {
      method: 'POST',
      headers: { applicationCode, 'application-code': applicationCode },
      body: {
        workflow_instance_stage_user_task_id: workflowInstanceStageUserTaskId,
        authorized_closure_approver_id: authorizedClosureApproverId || null,
        non_authorized_closure_approver_id: nonAuthorizedClosureApproverId || null,
        physical_file_movement_required: input.physicalFileMovementRequired,
        document_mover_id: input.physicalFileMovementRequired ? documentMoverId : null,
      },
    },
  );

  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) return { success: false, message: dataPayloadErrorMessage(res.data) };
  return { success: true, data: undefined };
}

export function closureApproverLabel(row: ClosureApproverUserRow): { id: string; label: string } | null {
  const r = row as unknown as Record<string, unknown>;
  const id = strField(r, 'user_id', 'userId');
  if (!id) return null;
  const name = strField(r, 'display_name', 'displayName');
  return { id, label: name || id };
}

export function nonAuthorizedClosureApproverLabel(row: ClosureApproverUserRow): { id: string; label: string } | null {
  const base = closureApproverLabel(row);
  if (!base) return null;
  const r = row as unknown as Record<string, unknown>;
  const designation = strField(r, 'designation');
  return { id: base.id, label: formatNameWithDesignation(base.label, designation) };
}

